<template>
  <div
    ref="keyboardInput"
    :class="classes(n())"
  >
    <div
      v-if="isShowPlaceHolder && placeholderPosition === 'left'"
      :class="classes(n('--placeholder'))"
      data-index="-1"
      @click="handleFocus"
    >
      {{ placeholder }}
    </div>
    <div
      v-for="(char, i) in chars"
      :key="i"
      :data-val="char"
      :data-index="i"
      :class="[classes(n('--item')), i === 0 ? classes(n('--begin')) : '']"
      @click="handleFocus"
    >
      <i
        :data-val="char"
        :data-index="i"
      >{{ char }}</i><i
        v-show="cursorPos === i"
        :class="[isFocus ? classes(n('--cursor')) : '']"
      />
    </div>
    <div
      v-if="isShowPlaceHolder && placeholderPosition === 'right'"
      :class="classes(n('--placeholder'))"
      data-index="-1"
      @click="handleFocus"
    >
      {{ placeholder }}
    </div>
    <Keyboard
      :visible="isShowKeyboard"
      :type="props.type"
      :decimal="props.decimal"
      :confirm-btn-text="props.confirmBtnText"
      :confirm-btn-enable="props.confirmBtnEnable"
      @input="handleInput"
      @delete="handleDelete"
      @confirm="handleConfirm"
      @blur="handleBlur"
    />
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'virtual-vue';
import { KeyboardInputProps } from './props';
import { KeyboardInputEmits } from './emits';
import { createNamespace } from '../utils/components';
import Keyboard from '../keyboard/index';
import { useKeyboardInput } from './hooks';

const { name, n, classes } = createNamespace('keyboard-input');

export default defineComponent({
  name,
  components: { Keyboard },
  props: KeyboardInputProps,
  emits: KeyboardInputEmits,
  setup(props, { emit }) {
    const { cursorPos, values, chars, initData, setCursorPos, insertValue, removeValue } = useKeyboardInput(props, emit);

    // 输入框是否聚焦
    const isFocus = ref(false);
    const isShowPlaceHolder = computed(() => props.placeholder && values.value.length === 0);
    const isShowKeyboard = computed(() => !props.disabled && isFocus.value);

    const keyboardInput = ref<HTMLDivElement | null>(null);

    // 初始化默认值
    initData();

    /** 处理输入事件 */
    const handleInput = (char: string) => {
      if (!props.disabled) {
        insertValue(char);
      }
    };

    /** 处理删除事件 */
    const handleDelete = () => {
      if (!props.disabled) {
        removeValue();
      }
    };

    /** 处理失焦事件 */
    const handleBlur = (event: Event) => {
      if (props.disabled) {
        return;
      }
      if (props.alwaysShowKeyboard) {
        return;
      }
      let target = event.target as HTMLElement;
      while (target && target !== document.documentElement) {
        if (target === keyboardInput.value) {
          return;
        }
        target = target.parentNode as HTMLElement;
      }
      isFocus.value = false;
      emit('blur');
    };

    /** 设置光标聚焦 */
    const focus = () => {
      if (!props.disabled && !isFocus.value) {
        isFocus.value = true;
        emit('focus');
      }
    };

    /** 光标聚焦 */
    const handleFocus = (e: Event) => {
      if (!props.disabled) {
        const target = e.target as HTMLElement;
        const index = Number.parseInt(target.getAttribute('data-index')!, 10);
        // -1表示点击到外框，此时需要将光标设置到最后
        setCursorPos(index === -1 ? undefined : index);
        focus();
      }
    };

    /** 确认提交 */
    const handleConfirm = () => {
      if (!props.disabled) {
        emit('confirm');
      }
    };

    return {
      n,
      classes,
      props,
      cursorPos,
      chars,
      isShowPlaceHolder,
      isShowKeyboard,
      isFocus,
      keyboardInput,
      focus,
      setCursorPos,
      handleFocus,
      handleInput,
      handleDelete,
      handleBlur,
      handleConfirm,
    };
  },
});

</script>
